import React from "react";
import { Award, PartyPopper, Star, Trophy, X } from "lucide-react";
import { Burst, Confetti, Fireworks } from "@/components/celebrations/Particles";
import { shortenNos } from "@/lib/caseNo";

const GOLD = ["#fbbf24", "#f59e0b", "#fde68a", "#facc15"];
const LOOK = {
  confetti: { testId: "celebration-confetti", Icon: Trophy, chip: "bg-sky-100 text-sky-500", backdrop: "bg-slate-900/40" },
  star: { testId: "celebration-star", Icon: Star, chip: "bg-amber-100 text-amber-500", backdrop: "bg-slate-900/40" },
  fireworks: { testId: "celebration-fireworks", Icon: PartyPopper, chip: "bg-violet-100 text-violet-600", backdrop: "bg-slate-950/70" },
};

function SlideCard({ item, onClose }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-[70] flex items-start justify-end p-4 sm:p-6" data-testid="celebration-slide-card">
      <div className="animate-slide-in-right pointer-events-auto mt-16 w-full max-w-sm rounded-2xl border border-amber-200 bg-white p-4 shadow-xl">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600"><Award className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-900">{item.title}</p>
            <p className="mt-0.5 text-sm text-slate-500">{shortenNos(item.message)}</p>
          </div>
          <button onClick={onClose} data-testid="celebration-dismiss-btn" className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"><X className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

// Full-screen celebrations: Confetti (awards), Stars (approvals), Fireworks (big firsts); plus the subtle demerit slide card.
export default function CelebrationCard({ item, onClose }) {
  if (item.celebration === "slide_card") return <SlideCard item={item} onClose={onClose} />;
  const look = LOOK[item.celebration] || LOOK.confetti;
  const isStar = item.celebration === "star";
  return (
    <div className={`fixed inset-0 z-[70] flex items-center justify-center ${look.backdrop} backdrop-blur-[2px]`} data-testid={look.testId} onClick={onClose}>
      {item.celebration === "confetti" && <Confetti />}
      {item.celebration === "fireworks" && <Fireworks />}
      {isStar && <><Burst x={50} y={46} colors={GOLD} count={16} radius={190} size={16} star /><Burst x={50} y={46} colors={GOLD} count={12} radius={120} size={10} star delay={0.25} /></>}
      <div className="relative mx-4 w-full max-w-sm animate-pop-in rounded-3xl bg-white p-8 text-center shadow-2xl">
        <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center">
          {isStar && <span className="absolute inset-0 animate-star-ray rounded-full bg-amber-300/60 blur-md" />}
          <span className={`relative flex h-20 w-20 items-center justify-center rounded-full ${look.chip} animate-star-burst`}>
            <look.Icon className={`h-10 w-10 ${isStar ? "fill-amber-400" : ""}`} />
          </span>
        </div>
        <h3 className="font-heading text-2xl font-extrabold text-slate-900" data-testid="celebration-title">{item.title}</h3>
        {item.message && <p className="mt-2 text-sm font-medium text-slate-500" data-testid="celebration-message">{shortenNos(item.message)}</p>}
        <button onClick={onClose} data-testid="celebration-dismiss-btn"
          className="mt-6 inline-flex items-center rounded-full bg-slate-900 px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-800">
          Awesome!
        </button>
      </div>
    </div>
  );
}
